import { useEffect, useState } from 'react';
import { client } from '@/api/client';
import { supabase } from '@/api/supabaseClient';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, Users, User, ExternalLink, CheckCircle2, Loader2, Clock, Sparkles, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// ----- pricing constants (must match Stripe Price objects) -----
const OWNER_OP_PRICE_USD = 79.99;
const FLEET_BASE_USD     = 140.00;   // covers up to 2 drivers
const FLEET_INCLUDED     = 2;        // drivers included in base
const FLEET_EXTRA_USD    = 59.99;    // per driver above FLEET_INCLUDED
const MIN_FLEET_SEATS    = 2;        // Fleet implies a real fleet
const MAX_SEATS          = 999;
const AI_DISPATCH_ADDON_PRICE_USD = 15.00;

// Graduated math: base fee covers the first FLEET_INCLUDED drivers,
// additional drivers add FLEET_EXTRA_USD each. Mirrors Stripe's tiered
// pricing config exactly so the displayed total matches the invoice.
const fleetMonthly = (seats) => {
  const s = Math.max(MIN_FLEET_SEATS, Math.min(seats, MAX_SEATS));
  return FLEET_BASE_USD + Math.max(0, s - FLEET_INCLUDED) * FLEET_EXTRA_USD;
};

const PACKAGES = [
  {
    id:          'owner_op',
    label:       'Owner-Operator',
    icon:        User,
    priceLabel:  '$79.99 / month',
    priceSuffix: 'flat',
    blurb:       'Solo owner-driver running their own truck. Load Board, Active Load, invoicing and fuel advances, plus P&L and IRP Miles reporting. No HR, payroll, multi-staff, or fleet maintenance module (you are the driver) — AI Dispatch is a $15/mo add-on.',
    seatsModel:  'flat',
  },
  {
    id:          'fleet',
    label:       'Fleet',
    icon:        Users,
    priceLabel:  '$140/mo (includes 2 drivers)',
    priceSuffix: 'includes 2 drivers, +$59.99/mo each additional',
    blurb:       'Multi-driver carrier with employee drivers. Everything in Owner-Op plus HR (DQF, applications, road tests), Payroll (settlements, batches), and multi-staff invites for Admin / HR / Accounting / Dispatch.',
    seatsModel:  'graduated',
  },
];

// Row-by-row feature matrix -- the honest answer to "what am I actually
// giving up" instead of making someone infer it from the two blurbs
// above. ownerOp/fleet: true = included, false = not included, string =
// included but scoped differently (shown as-is instead of a check/x).
const FEATURE_MATRIX = [
  { label: 'Load Board & Active Load',        ownerOp: true,  fleet: true },
  { label: 'Invoicing & fuel advances',        ownerOp: true,  fleet: true },
  { label: 'Team Chat',                        ownerOp: false, fleet: true },
  { label: 'Reports',                          ownerOp: 'P&L + IRP Miles', fleet: 'Full suite (~25 reports)' },
  { label: 'AI Dispatch',                      ownerOp: '$15/mo add-on', fleet: 'Included' },
  { label: 'Driver Pay Mgt (your settlement history)', ownerOp: true, fleet: true },
  { label: 'Payroll batch runs (multi-driver)', ownerOp: false, fleet: true },
  { label: 'Multi-driver Dispatch board',      ownerOp: false, fleet: true },
  { label: 'HR (applications, DQF, road tests)', ownerOp: false, fleet: true },
  { label: 'Maintenance & service records',    ownerOp: false, fleet: true },
  { label: 'Multi-staff invites (Admin/HR/Accounting/Dispatch)', ownerOp: false, fleet: true },
  { label: 'Price',                            ownerOp: `$${OWNER_OP_PRICE_USD.toFixed(2)}/mo flat`, fleet: `$${FLEET_BASE_USD.toFixed(2)}/mo (${FLEET_INCLUDED} drivers) + $${FLEET_EXTRA_USD.toFixed(2)}/mo each additional` },
];

const MatrixCell = ({ value }) => {
  if (value === true) return <Check className="w-4 h-4 text-green-400 mx-auto" />;
  if (value === false) return <X className="w-4 h-4 text-muted-foreground/40 mx-auto" />;
  return <span className="text-xs text-foreground">{value}</span>;
};

const STATUS_BADGE_COLORS = {
  trialing: 'bg-blue-500/10   text-blue-300   border-blue-500/30',
  active:   'bg-green-500/10  text-green-300  border-green-500/30',
  past_due: 'bg-red-500/10    text-red-300    border-red-500/30',
  canceled: 'bg-gray-500/10   text-gray-300   border-gray-500/30',
};

const formatDate = (iso) => iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—';

const daysBetween = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso) - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
};

const formatMoney = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function BillingTab() {
  const { carrierPlan, refreshUser } = useAuth();
  const [snap, setSnap]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(null);    // { plan, action } or 'portal' or 'ai-dispatch'
  const [seats, setSeats]     = useState(MIN_FLEET_SEATS);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('my_billing_snapshot');
      if (error) throw error;
      const row = data?.[0] || null;
      setSnap(row);
      // Seed the Fleet seat input from actual driver count, floored at min.
      if (row?.seat_count != null) {
        setSeats(Math.max(MIN_FLEET_SEATS, row.seat_count));
      }
    } catch (e) {
      toast.error(e?.message || 'Could not load billing.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // First-time subscribe: creates a new Stripe Checkout Session.
  const onSubscribe = async (plan) => {
    setBusy({ plan, action: 'subscribe' });
    try {
      const body = plan === 'fleet' ? { plan, seats } : { plan };
      const { url } = await client.functions.invoke('create-checkout-session', body);
      if (url) window.location.href = url;
      else toast.error('Checkout failed (no URL returned).');
    } catch (e) {
      toast.error(e?.message || 'Could not start checkout.');
    } finally {
      setBusy(null);
    }
  };

  // Plan switch on an existing subscription: in-place Stripe sub update
  // with native proration (Stripe credits unused time on the old plan
  // toward the next invoice; no second checkout session). Frontend then
  // refreshes auth + billing so the sidebar gating updates immediately.
  const onSwitch = async (plan) => {
    const targetSeats = plan === 'fleet' ? seats : 1;
    const fleetTotal  = fleetMonthly(targetSeats);
    const confirmMsg  = plan === 'fleet'
      ? `Switch to Fleet at ${targetSeats} driver${targetSeats === 1 ? '' : 's'} — ${formatMoney(fleetTotal)} / mo?\n\nStripe will credit unused Owner-Op days against your first Fleet invoice.`
      : `Switch to Owner-Operator at ${formatMoney(OWNER_OP_PRICE_USD)} / mo?\n\nStripe will credit unused Fleet days against your first Owner-Op invoice. HR, Payroll, and the multi-driver Dispatch board will be hidden from your sidebar.`;
    if (!window.confirm(confirmMsg)) return;

    setBusy({ plan, action: 'switch' });
    try {
      const body = plan === 'fleet' ? { plan, seats: targetSeats } : { plan };
      await client.functions.invoke('change-subscription-plan', body);
      toast.success(plan === 'fleet' ? 'Switched to Fleet.' : 'Switched to Owner-Operator.');
      // refreshUser re-runs loadSupabaseUser -> picks up the new
      // carriers.plan -> Layout's sidebar gating reflows immediately.
      await refreshUser();
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not switch plan.');
    } finally {
      setBusy(null);
    }
  };

  // Adds/removes the $15/mo AI Dispatch add-on as a second item on the
  // existing subscription -- independent of the base-plan Subscribe/Switch
  // flow above, so toggling it doesn't touch plan or seat count.
  const onToggleAiDispatch = async (enable) => {
    setBusy('ai-dispatch');
    try {
      await client.functions.invoke('toggle-ai-dispatch-addon', { enable });
      toast.success(enable ? 'AI Dispatch added — $15/mo.' : 'AI Dispatch add-on removed.');
      await refreshUser();
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not update the AI Dispatch add-on.');
    } finally {
      setBusy(null);
    }
  };

  const onManageBilling = async () => {
    setBusy('portal');
    try {
      const { url } = await client.functions.invoke('create-portal-session', {});
      if (url) window.location.href = url;
      else toast.error('Could not open billing portal.');
    } catch (e) {
      toast.error(e?.message || 'Could not open billing portal.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }
  if (!snap) {
    return <p className="text-sm text-muted-foreground">No billing data available.</p>;
  }

  const currentPlan     = snap.plan || carrierPlan || 'fleet';
  const trialDaysLeft   = snap.subscription_status === null ? daysBetween(snap.trial_ends_at) : null;
  const hasSubscription = !!snap.stripe_subscription_id;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-foreground">Billing</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Manage your Pro Haul subscription, payment method, and driver count.
        </p>
      </div>

      {/* ── Status row ── */}
      <div className="glass-card p-5 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-amber-500/10 text-amber-300 border-amber-500/30">
            {currentPlan === 'owner_op' ? 'Pro Haul — Owner-Operator' : 'Pro Haul — Fleet'}
          </span>
          {snap.subscription_status && (
            <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full border capitalize', STATUS_BADGE_COLORS[String(snap.subscription_status)] || 'bg-muted/40 text-muted-foreground border-border')}>
              {String(snap.subscription_status).replace('_',' ')}
            </span>
          )}
          {snap.cancel_at_period_end && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-amber-500/10 text-amber-300 border-amber-500/30">
              cancels at period end
            </span>
          )}
        </div>

        {snap.subscription_status === null && trialDaysLeft !== null && (
          <div className="flex items-start gap-2 text-sm">
            <Clock className="w-4 h-4 text-blue-300 mt-0.5 flex-shrink-0" />
            <span className="text-foreground">
              <strong>{trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left in your free trial.</strong>{' '}
              <span className="text-muted-foreground">Subscribe before {formatDate(snap.trial_ends_at)} to keep all features.</span>
            </span>
          </div>
        )}
        {snap.subscription_status && snap.current_period_end && (
          <div className="flex items-start gap-2 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <span className="text-foreground">
              Current period ends <strong>{formatDate(snap.current_period_end)}</strong>
              {snap.cancel_at_period_end
                ? <span className="text-amber-300"> — subscription will end then.</span>
                : <span className="text-muted-foreground"> — renews automatically.</span>}
            </span>
          </div>
        )}

        {snap.seat_count != null && currentPlan === 'fleet' && (
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span className="text-foreground">
              <strong>{snap.seat_count}</strong> active driver{snap.seat_count === 1 ? '' : 's'}
              <span className="text-muted-foreground"> on this carrier today</span>
            </span>
          </div>
        )}

        {hasSubscription && (
          <Button
            variant="outline"
            onClick={onManageBilling}
            disabled={busy === 'portal'}
            className="border-border"
          >
            {busy === 'portal'
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening portal…</>
              : <><CreditCard className="w-4 h-4 mr-2" /> Manage billing in Stripe <ExternalLink className="w-3 h-3 ml-1.5" /></>}
          </Button>
        )}
      </div>

      {/* ── Package picker ── */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          {hasSubscription ? 'Your plan' : 'Choose your plan'}
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PACKAGES.map(pkg => {
            const Icon       = pkg.icon;
            const isCurrent  = hasSubscription && currentPlan === pkg.id;
            const isBusyHere = busy?.plan === pkg.id;
            // Live monthly total used on the action button label.
            const estTotal   = pkg.id === 'fleet' ? fleetMonthly(seats) : OWNER_OP_PRICE_USD;
            // Routing: switch when there's already a sub, otherwise subscribe.
            const onClick    = hasSubscription
              ? () => onSwitch(pkg.id)
              : () => onSubscribe(pkg.id);

            const buttonLabel = isCurrent
              ? 'Current plan'
              : hasSubscription
                ? `Switch to ${pkg.label}`
                : `Subscribe — ${formatMoney(estTotal)} / mo`;

            return (
              <div key={pkg.id} className={cn(
                'glass-card p-5 flex flex-col gap-3',
                isCurrent && 'border border-primary/40'
              )}>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                    <span className="font-semibold text-foreground">{pkg.label}</span>
                    {isCurrent && <CheckCircle2 className="w-4 h-4 text-primary" />}
                  </div>
                  <p className="text-2xl font-space font-bold text-foreground">{pkg.priceLabel}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{pkg.priceSuffix}</p>
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed flex-1">{pkg.blurb}</p>

                {/* Fleet seat input: shown when subscribing OR switching to Fleet.
                    Hidden only when Fleet is the current plan (manage via portal). */}
                {pkg.seatsModel === 'graduated' && !isCurrent && (
                  <div className="flex items-end gap-2 pt-2 border-t border-border">
                    <div className="space-y-1.5">
                      <Label htmlFor={`seats-${pkg.id}`} className="text-xs">Total drivers</Label>
                      <Input
                        id={`seats-${pkg.id}`}
                        type="number"
                        min={MIN_FLEET_SEATS}
                        max={MAX_SEATS}
                        value={seats}
                        onChange={e => {
                          const n = parseInt(e.target.value, 10);
                          setSeats(
                            Number.isFinite(n) && n >= MIN_FLEET_SEATS
                              ? Math.min(n, MAX_SEATS)
                              : MIN_FLEET_SEATS,
                          );
                        }}
                        className="w-24"
                      />
                    </div>
                    <div className="text-xs text-muted-foreground pb-2.5 leading-tight">
                      {seats <= FLEET_INCLUDED
                        ? <>= <span className="font-mono text-foreground font-semibold">{formatMoney(FLEET_BASE_USD)}</span> / mo (base)</>
                        : <>
                            {formatMoney(FLEET_BASE_USD)} + {seats - FLEET_INCLUDED} × {formatMoney(FLEET_EXTRA_USD)} =
                            <span className="ml-1 font-mono text-foreground font-semibold">
                              {formatMoney(estTotal)}
                            </span> / mo
                          </>}
                    </div>
                  </div>
                )}

                <Button
                  onClick={onClick}
                  disabled={isCurrent || isBusyHere}
                  className={cn(
                    'w-full',
                    isCurrent ? 'bg-muted/60 text-muted-foreground cursor-not-allowed' : 'bg-primary text-primary-foreground'
                  )}
                >
                  {isBusyHere
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {busy?.action === 'switch' ? 'Switching…' : 'Redirecting…'}</>
                    : buttonLabel}
                </Button>

                {isCurrent && pkg.seatsModel === 'graduated' && (
                  <p className="text-xs text-muted-foreground">
                    Update seat count via <strong>Manage billing in Stripe</strong>.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-3">
          {!hasSubscription && 'Have a promo code? Enter it on the Stripe Checkout page after clicking Subscribe. '}
          {hasSubscription && currentPlan === 'owner_op' && 'Hired your first driver? Switch to Fleet — Stripe credits unused Owner-Op days against your first Fleet invoice.'}
          {hasSubscription && currentPlan === 'fleet'    && 'Down to one driver? Switch to Owner-Operator — Stripe credits unused Fleet days against your first Owner-Op invoice.'}
        </p>
      </div>

      {/* ── Feature comparison ── */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">Compare plans</h4>
        <div className="glass-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left font-medium text-muted-foreground px-4 py-2.5">Feature</th>
                <th className="text-center font-medium text-muted-foreground px-4 py-2.5 w-36">Owner-Operator</th>
                <th className="text-center font-medium text-muted-foreground px-4 py-2.5 w-48">Fleet</th>
              </tr>
            </thead>
            <tbody>
              {FEATURE_MATRIX.map(row => (
                <tr key={row.label} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5 text-foreground">{row.label}</td>
                  <td className="px-4 py-2.5 text-center"><MatrixCell value={row.ownerOp} /></td>
                  <td className="px-4 py-2.5 text-center"><MatrixCell value={row.fleet} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── AI Dispatch add-on (Owner-Op only) ── */}
      {hasSubscription && currentPlan === 'owner_op' && (
        <div className="glass-card p-5 flex items-start gap-4">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-foreground">AI Dispatch</span>
              {snap.ai_dispatch_addon && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-green-500/10 text-green-300 border-green-500/30">
                  Active
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              The in-app chat assistant on your Load Board and Active Load pages — ask it to find loads, check rates, or walk through your next stop. {formatMoney(AI_DISPATCH_ADDON_PRICE_USD)}/mo, on top of your Owner-Operator plan.
            </p>
          </div>
          <Button
            variant={snap.ai_dispatch_addon ? 'outline' : 'default'}
            onClick={() => onToggleAiDispatch(!snap.ai_dispatch_addon)}
            disabled={busy === 'ai-dispatch'}
            className={cn(!snap.ai_dispatch_addon && 'bg-primary text-primary-foreground', 'flex-shrink-0')}
          >
            {busy === 'ai-dispatch'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : snap.ai_dispatch_addon ? 'Remove' : `Add — ${formatMoney(AI_DISPATCH_ADDON_PRICE_USD)}/mo`}
          </Button>
        </div>
      )}
    </div>
  );
}
